/* eslint-disable no-unused-vars */
import { useCallback, useContext } from 'react';

import { ApolloError } from '@apollo/client';

import ReCaptchaContext from '@/contexts/ReCaptchaContext';
import { logWarn } from '@/helpers/logger/logUtils';
import { isRecaptchaError } from '@/helpers/recaptchaUtils';

export const RECAPTCHA_V3_FORCE_FAIL_STORAGE_KEY = 'forceRecaptchaV3Failure';

type ReCaptchaCallback = {
  action: string;
  callback: (rcToken: string, ...props: any[]) => void;
  onV3Failure?: () => void;
};

const useReCaptchaCallback = <T extends unknown[] = unknown[]>({
  action,
  callback,
  onV3Failure,
}: ReCaptchaCallback): ((...props: T) => void) => {
  const { scoredBasedKey } = useContext(ReCaptchaContext);

  return useCallback(
    (...props: T) => {
      const isForcedV3Failure =
        process.env.NODE_ENV !== 'production' &&
        window.localStorage?.getItem(RECAPTCHA_V3_FORCE_FAIL_STORAGE_KEY) === 'true';

      if (isForcedV3Failure) {
        logWarn('RecaptchaV3 failure forced for testing');
        onV3Failure?.();
        return;
      }

      window.grecaptcha?.enterprise.ready(async () => {
        try {
          const rcToken = await window.grecaptcha?.enterprise.execute(scoredBasedKey, { action });

          if (rcToken) {
            callback(rcToken, ...props);
          }
        } catch (error) {
          if (isRecaptchaError(error as ApolloError) && onV3Failure) {
            logWarn(`RecaptchaV3 error occurred: ${(error as Error).message}`);
            onV3Failure();
          } else {
            logWarn(`Recaptcha error occurred: ${(error as Error).message}`);
            throw error;
          }
        }
      });
    },
    [action, callback, onV3Failure, scoredBasedKey],
  );
};

export default useReCaptchaCallback;
